import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { parseErrorDetail, showLimitError } from '@/lib/utils';
import {
  Bell,
  Plus,
  Send,
  Clock,
  Edit2,
  Trash2,
  Users,
  AlertCircle,
  CheckCircle,
  Info,
  Megaphone,
  Calendar,
  Eye,
  BarChart3,
  X,
  User,
  RefreshCw,
  Monitor,
  Mail,
  MousePointerClick,
} from 'lucide-react';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { LimitMeter } from '@/components/LimitMeter';
import { DataTablePagination, DataTableSkeleton, DataTableToolbar } from '@/components/DataTableControls';
import { EmptyState } from '@/components/EmptyState';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NOTIFICATION_TYPES = [
  { value: 'info', label: 'Information', icon: Info, color: 'blue' },
  { value: 'urgent', label: 'Urgent', icon: AlertCircle, color: 'red' },
  { value: 'reminder', label: 'Reminder', icon: Clock, color: 'yellow' },
  { value: 'success', label: 'Success', icon: CheckCircle, color: 'green' },
  { value: 'announcement', label: 'Announcement', icon: Megaphone, color: 'purple' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// Mirrors backend _validate_action_url (EIP-236): forgive slash/backslash typos
// after the scheme, prepend https:// to bare domains, return null when the URL
// can't be made valid. Backslashes in a query/fragment are data — reject, never
// rewrite.
const normalizeActionUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const splitAt = Math.min(...['?', '#'].map((c) => {
    const i = raw.indexOf(c);
    return i === -1 ? raw.length : i;
  }));
  const head = raw.slice(0, splitAt).replace(/\\/g, '/').replace(/^(https?):\/+/i, '$1://');
  const candidate0 = head + raw.slice(splitAt);
  if (candidate0.includes('\\')) return null;
  if (candidate0.startsWith('//')) return null;
  if (candidate0.startsWith('/')) return candidate0;
  let candidate = candidate0;
  if (!/^https?:\/\//i.test(candidate0)) {
    const firstSegment = candidate0.split('/')[0];
    if (firstSegment.includes(':') || firstSegment.includes('@')) return null;
    candidate = `https://${candidate0}`;
  }
  const authority = candidate.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
  if (authority.includes('@')) return null;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname || parsed.hostname === 'http' || parsed.hostname === 'https') return null;
  } catch {
    return null;
  }
  return candidate;
};

function isNotificationLimitError(detail) {
  const parsed = parseErrorDetail(detail);
  const message = String(parsed.message || '').toLowerCase();
  return parsed.error === 'notification_limit_reached'
    || message.includes('notification capacity reached')
    || message.includes('notification capacity')
    || message.includes('notification limit');
}

async function readErrorPayload(response, fallbackMessage) {
  try {
    const payload = await response.json();
    return payload?.detail ?? payload ?? fallbackMessage;
  } catch (_error) {
    try {
      const text = await response.text();
      return text || fallbackMessage;
    } catch (_textError) {
      return fallbackMessage;
    }
  }
}

function showNotificationLimitToast(detail, canManagePlan) {
  const parsed = parseErrorDetail(detail);
  const baseMessage = parsed.message || 'Notification capacity reached for this billing window.';
  const waitMessage = 'You can also wait until older notifications roll out of the 30-day window.';
  showLimitError(
    {
      ...parsed,
      message: canManagePlan ? `${baseMessage} Upgrade your plan or buy capacity to continue. ${waitMessage}` : baseMessage,
      upgrade_url: '/settings/billing',
      cta_label: 'Upgrade Plan',
    },
    null,
    'Notification limit reached',
    {
      canUseCta: canManagePlan,
      nonCtaMessage: `Ask an admin to upgrade or buy capacity. ${waitMessage}`,
    }
  );
}

function reportSendResult(data) {
  const delivered = data.delivery_count || 0;
  const plural = delivered !== 1 ? 's' : '';
  const failed = data.email_failed_count || 0;

  // The send response has always carried an email count, and the UI has always ignored
  // it — so a campaign where every single email bounced still reported
  // "Notification sent to 25 users!". That silence is the reason EIP-304 went unnoticed
  // for days, so email trouble is now surfaced even though the in-app leg succeeded.
  if (data.email_disabled) {
    toast.success(`Notification sent to ${delivered} user${plural} in Eva People.`);
    toast.error('Email was not sent: the email integration is disabled.');
    return;
  }
  if (failed > 0) {
    toast.success(`Notification sent to ${delivered} user${plural} in Eva People.`);
    toast.error(
      `${failed} email${failed !== 1 ? 's' : ''} failed to send. Check the notification's delivery details.`
    );
    return;
  }
  toast.success(`Notification sent to ${delivered} user${plural}!`);
}

function isCapacityAtLimit(capacity) {
  if (!capacity || capacity.is_unlimited || capacity.limit == null) return false;
  return Number(capacity.used || 0) >= Number(capacity.limit);
}

function buildNotificationCapacityMessage(capacity) {
  const used = Number(capacity?.used || 0);
  const limit = Number(capacity?.limit || 0);
  const days = Number(capacity?.period_days || 30);
  if (limit > 0) {
    return `Notification capacity reached: ${used} of ${limit} notifications sent in the last ${days} days.`;
  }
  return 'Notification capacity reached for this billing window.';
}

function NotificationStats({ stats }) {
  if (!stats) return null;

  const total = Number(stats.total || 0);
  const read = Number(stats.read || 0);
  const inAppTotal = Number(stats.in_app_total || 0);
  const emailTotal = Number(stats.email_total || 0);
  const channelStats = [
    {
      label: 'People',
      value: inAppTotal > 0 ? `${Number(stats.in_app_read || 0)}/${inAppTotal}` : '—',
      Icon: Monitor,
    },
    {
      label: 'Email',
      value: emailTotal > 0 ? `${Number(stats.email_opened || 0)}/${emailTotal}` : '—',
      Icon: Mail,
    },
    {
      label: 'CTA',
      value: emailTotal > 0 ? Number(stats.clicked || 0) : '—',
      Icon: MousePointerClick,
    },
  ];

  return (
    <div className="space-y-1.5">
      <div className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{read}</span>
        /{total} read
      </div>
      <div className="grid w-fit grid-cols-3 gap-1.5">
        {channelStats.map(({ label, value, Icon }) => (
          <span
            key={label}
            className="inline-flex min-w-[4.75rem] items-center gap-1 rounded-md border border-border/70 bg-muted/40 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground"
          >
            <Icon className="h-3 w-3 shrink-0" />
            <span className="truncate">{label}</span>
            <span className="ml-auto text-foreground/80">{value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function formatNotificationDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function getPeopleStatus(delivery) {
  if (delivery.in_app_clicked_at) return 'Clicked';
  if (delivery.in_app_read_at || delivery.read_at) return 'Read';
  if (delivery.delivered_at) return 'Delivered';
  return delivery.status || 'Pending';
}

function getEmailStatus(delivery) {
  if (delivery.email_clicked_at) return 'Clicked';
  if (delivery.email_opened_at) return 'Opened';
  if (delivery.delivered_at) return 'Delivered';
  return delivery.status || 'Pending';
}

function isPeopleDelivery(delivery) {
  return delivery.channel === 'in_app' || delivery.channel === 'both';
}

function isEmailDelivery(delivery) {
  return delivery.channel === 'email' || delivery.channel === 'both';
}

function ChannelDeliveryTable({ deliveries, emptyMessage, columns }) {
  if (!deliveries.length) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {columns.map((column) => (
              <th key={column.header} className="px-3 py-2 text-left">{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {deliveries.map((delivery) => (
            <tr key={delivery.id} className="border-t border-border/60">
              {columns.map((column) => (
                <td key={column.header} className="px-3 py-2 text-muted-foreground">
                  {column.render(delivery)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationActionLink({ url, label = 'Open link' }) {
  if (!url) return '—';
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
    >
      {label || 'Open link'}
      <MousePointerClick className="h-3 w-3" />
    </a>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

function NotificationDetailsSheet({ notification, open, loading, error, onOpenChange, getTypeIcon, getStatusBadge, getTargetLabel }) {
  const deliveries = notification?.deliveries || [];
  const peopleDeliveries = deliveries.filter(isPeopleDelivery);
  const emailDeliveries = deliveries.filter(isEmailDelivery);
  const hasAction = Boolean(notification?.action_url);
  const actionLabel = notification?.action_label || 'Open link';
  const peopleColumns = [
    { header: 'Recipient', render: (delivery) => <span className="text-foreground">{delivery.user_email || delivery.user_id}</span> },
    { header: 'Status', render: getPeopleStatus },
    { header: 'Delivered', render: (delivery) => formatNotificationDate(delivery.delivered_at) },
    { header: 'Read', render: (delivery) => formatNotificationDate(delivery.in_app_read_at || delivery.read_at) },
    ...(hasAction ? [
      { header: 'CTA', render: () => <NotificationActionLink url={notification.action_url} label={actionLabel} /> },
      { header: 'Clicked', render: (delivery) => formatNotificationDate(delivery.in_app_clicked_at) },
    ] : []),
  ];
  const emailColumns = [
    { header: 'Recipient', render: (delivery) => <span className="text-foreground">{delivery.user_email || delivery.user_id}</span> },
    { header: 'Status', render: getEmailStatus },
    { header: 'Delivered', render: (delivery) => formatNotificationDate(delivery.delivered_at) },
    { header: 'Opened', render: (delivery) => formatNotificationDate(delivery.email_opened_at) },
    ...(hasAction ? [
      { header: 'CTA', render: () => <NotificationActionLink url={notification.action_url} label={actionLabel} /> },
      { header: 'Clicked', render: (delivery) => formatNotificationDate(delivery.email_clicked_at) },
    ] : []),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="pr-8">
          <SheetTitle>{notification?.title || 'Notification details'}</SheetTitle>
          <SheetDescription>{notification?.message || 'Delivery and engagement details.'}</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="space-y-3 pt-6" data-testid="notification-details-loading">
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
            <div className="h-32 animate-pulse rounded-lg bg-muted" />
            <div className="h-48 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : notification ? (
          <div className="space-y-6 pt-6">
            <section className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1 text-sm text-muted-foreground">
                  {getTypeIcon(notification.type)}
                  <span className="capitalize">{notification.type}</span>
                </span>
                {getStatusBadge(notification.status)}
              </div>
              <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{notification.message}</p>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Configuration</h3>
              <dl className="grid grid-cols-1 gap-4 rounded-lg border border-border p-4 sm:grid-cols-2">
                <DetailField label="Target" value={getTargetLabel(notification.target_type, notification.target_ids)} />
                <DetailField label="Channel" value={String(notification.channel || '—').replace('_', ' ')} />
                <DetailField label="Created" value={formatNotificationDate(notification.created_at)} />
                <DetailField label="Sent" value={formatNotificationDate(notification.sent_at)} />
                <DetailField label="Scheduled" value={formatNotificationDate(notification.scheduled_at)} />
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">CTA</dt>
                  <dd className="mt-1 text-sm text-foreground">
                    <NotificationActionLink url={notification.action_url} label={notification.action_label || notification.action_url} />
                  </dd>
                </div>
              </dl>
            </section>

            {notification.delivery_stats ? (
              <section>
                <h3 className="mb-3 text-sm font-semibold text-foreground">Engagement</h3>
                <NotificationStats stats={notification.delivery_stats} />
              </section>
            ) : null}

            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Channel Details</h3>
              <Tabs defaultValue="people" className="space-y-3">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="people" className="gap-2">
                    <Monitor className="h-3.5 w-3.5" />
                    People
                  </TabsTrigger>
                  <TabsTrigger value="email" className="gap-2">
                    <Mail className="h-3.5 w-3.5" />
                    Email
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="people">
                  <ChannelDeliveryTable
                    deliveries={peopleDeliveries}
                    emptyMessage="No People delivery records for this notification."
                    columns={peopleColumns}
                  />
                </TabsContent>

                <TabsContent value="email">
                  <ChannelDeliveryTable
                    deliveries={emailDeliveries}
                    emptyMessage="No email delivery records for this notification."
                    columns={emailColumns}
                  />
                </TabsContent>
              </Tabs>
            </section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

export default function NotificationsPage() {
  const { token, hasRole, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailNotification, setDetailNotification] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ConfirmEl, confirm] = useConfirm();
  const canManageNotifications = hasRole(['admin', 'approver']);
  const canManagePlan = hasRole(['admin']) || user?.is_superadmin;

  const fetchNotifications = useCallback(async (showRefreshing = false) => {
    if (!canManageNotifications) {
      setLoading(false);
      return;
    }
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canManageNotifications, token]);

  const fetchAnalytics = useCallback(async () => {
    if (!canManageNotifications) return;
    try {
      const response = await fetch(`${API_URL}/api/notifications/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, [canManageNotifications, token]);

  const refreshNotificationData = useCallback((showRefreshing = false) => {
    fetchNotifications(showRefreshing);
    fetchAnalytics();
  }, [fetchNotifications, fetchAnalytics]);

  useEffect(() => {
    refreshNotificationData();
  }, [refreshNotificationData]);

  // Auto-refresh every 30 seconds when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      refreshNotificationData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshNotificationData]);

  const handleRefresh = () => {
    refreshNotificationData(true);
  };

  const handleOpenDetails = async (notification) => {
    setDetailOpen(true);
    setDetailNotification(notification);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notification.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDetailNotification({
          ...notification,
          ...data,
          delivery_stats: notification.delivery_stats,
        });
      } else {
        setDetailError('Failed to load notification details.');
      }
    } catch (error) {
      console.error('Failed to load notification details:', error);
      setDetailError('Failed to load notification details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSend = async (notificationId) => {
    if (isCapacityAtLimit(notificationCapacity)) {
      showNotificationLimitToast(buildNotificationCapacityMessage(notificationCapacity), canManagePlan);
      return;
    }

    const ok = await confirm({ title: 'Send Notification', description: 'Send this notification now?', confirmLabel: 'Send' });
    if (!ok) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        reportSendResult(data);
        refreshNotificationData();
      } else {
        const error = await readErrorPayload(response, 'Failed to send notification');
        if (isNotificationLimitError(error)) {
          showNotificationLimitToast(error, canManagePlan);
        } else {
          toast.error(parseErrorDetail(error).message || 'Failed to send notification');
        }
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('Failed to send notification');
    }
  };

  const handleDelete = async (notificationId) => {
    const ok = await confirm({ title: 'Delete Notification', description: 'Delete this notification?', confirmLabel: 'Delete', variant: 'delete' });
    if (!ok) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Notification deleted');
        if (detailNotification?.id === notificationId) {
          setDetailOpen(false);
          setDetailNotification(null);
        }
        refreshNotificationData();
      } else {
        toast.error('Failed to delete notification');
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleSchedule = async (notificationId, scheduledAt) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}/schedule?scheduled_at=${encodeURIComponent(scheduledAt)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Notification scheduled!');
        refreshNotificationData();
      } else {
        toast.error('Failed to schedule notification');
      }
    } catch (error) {
      console.error('Failed to schedule notification:', error);
      toast.error('Failed to schedule notification');
    }
  };

  const filteredNotifications = useMemo(() => notifications.filter(n => {
    if (filter !== 'all' && n.status !== filter) return false;
    const query = searchQuery.trim().toLowerCase();
    if (query && ![
      n.title,
      n.message,
      n.type,
      n.status,
      n.target_type,
    ].some((value) => String(value || '').toLowerCase().includes(query))) return false;
    return true;
  }), [filter, notifications, searchQuery]);

  const totalPages = Math.max(Math.ceil(filteredNotifications.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const pagedNotifications = filteredNotifications.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const analyticsSummary = analytics || {
    total_sent: 0,
    total_deliveries: 0,
    read_rate: 0,
    click_rate: 0,
    capacity: {
      used: 0,
      limit: null,
      remaining: null,
      percent_used: 0,
      period_days: 30,
      is_unlimited: true,
    },
  };
  const notificationCapacity = analyticsSummary.capacity || {};
  const resetTablePage = () => setPage(1);

  if (!canManageNotifications) {
    return (
      <div className="p-6">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertCircle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Notifications are only available to approvers and administrators.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getTargetLabel = (targetType, targetIds) => {
    if (targetType === 'users' && targetIds && targetIds.length === 1) {
      return 'user';
    }
    return targetType;
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sending: 'bg-blue-100 text-blue-700',
      sent: 'bg-green-100 text-green-700',
      // A scheduled send the worker declined because it was too far overdue. Styled as
      // a problem, not as a grey 'draft' — the fallback below would otherwise render a
      // row labelled "Missed" in draft colours.
      missed: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const typeConfig = NOTIFICATION_TYPES.find(t => t.value === type);
    if (!typeConfig) return <Info className="w-4 h-4" />;
    const Icon = typeConfig.icon;
    return <Icon className={`w-4 h-4 text-${typeConfig.color}-500`} />;
  };

  return (
    <PageShell maxWidth="7xl" data-testid="notifications-page">
      {ConfirmEl}
      <PageBanner pageKey="engagement-notifications" />
      
      <PageHeader
        title="Notifications"
        className="mb-6"
        description="Create and manage notifications for your organization"
        actions={(
          <Button
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-4 h-4" />
            Create Notification
          </Button>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard title="Sent (30d)" value={analyticsSummary.total_sent} icon={Send} color="primary" />
        <KpiCard title="Deliveries" value={analyticsSummary.total_deliveries} icon={Users} color="info" />
        <KpiCard title="Read Rate" value={`${analyticsSummary.read_rate}%`} icon={Eye} color="success" />
        <KpiCard title="Click Rate" value={`${analyticsSummary.click_rate}%`} icon={BarChart3} color="warning" />
      </div>

      <LimitMeter
        title="Notification capacity"
        used={notificationCapacity.used || 0}
        limit={notificationCapacity.is_unlimited ? null : notificationCapacity.limit}
        percentUsed={notificationCapacity.percent_used}
        icon={Bell}
        displayType="capacity"
        caption={`Rolling ${notificationCapacity.period_days || 30}-day plan capacity`}
        showManagePlanCta
        testId="notification-limit-meter"
      />

      <Card className="border-border bg-card shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Notification Workspace</CardTitle>
          <CardDescription>Search, filter, schedule, and send employee notifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTableToolbar
            searchValue={searchQuery}
            onSearchChange={(value) => {
              setSearchQuery(value);
              resetTablePage();
            }}
            searchPlaceholder="Search notifications..."
            className="border-0 bg-transparent p-0"
          >
            <Select value={filter} onValueChange={(value) => { setFilter(value); resetTablePage(); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="notification-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex h-9 items-center gap-2 rounded-md border border-input px-3 text-sm text-muted-foreground">
              <Checkbox
                checked={autoRefresh}
                onCheckedChange={(checked) => setAutoRefresh(checked === true)}
              />
              Auto-refresh
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              title={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </DataTableToolbar>

          {loading ? (
            <DataTableSkeleton
              rows={5}
              columns={5}
              showToolbar={false}
              data-testid="notifications-loading"
            />
          ) : filteredNotifications.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="No notifications found"
              description="Create a notification to send timely updates to employees."
              actionLabel="Create notification"
              actionIcon={Plus}
              onAction={() => setShowCreateModal(true)}
              variant="panel"
            />
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[900px] data-table">
                  <thead className="bg-muted/50">
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notification</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Target</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stats</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedNotifications.map((notification) => (
                      <tr
                        key={notification.id}
                        className="cursor-pointer border-b border-border/50 hover:bg-secondary/30 focus-within:bg-secondary/30"
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for ${notification.title}`}
                        data-testid={`notification-row-${notification.id}`}
                        onClick={() => handleOpenDetails(notification)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpenDetails(notification);
                          }
                        }}
                      >
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-foreground">{notification.title}</p>
                            <p className="max-w-xs truncate text-sm text-muted-foreground">{notification.message}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(notification.type)}
                            <span className="text-sm capitalize text-muted-foreground">{notification.type}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {notification.target_type === 'users' && (!notification.target_ids || notification.target_ids.length === 1) ? (
                              <User className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <Users className="w-4 h-4 text-muted-foreground" />
                            )}
                            <span className="text-sm capitalize text-muted-foreground">
                              {getTargetLabel(notification.target_type, notification.target_ids)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(notification.status)}
                        </td>
                        <td className="px-4 py-4">
                          {notification.status === 'sent' && notification.delivery_stats ? (
                            <NotificationStats stats={notification.delivery_stats} />
                          ) : notification.status === 'scheduled' ? (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {notification.scheduled_at && !isNaN(new Date(notification.scheduled_at)) ? new Date(notification.scheduled_at).toLocaleDateString() : 'Pending'}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {notification.status === 'draft' && (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleSend(notification.id);
                                  }}
                                  title="Send Now"
                                >
                                  <Send className="w-4 h-4 text-success" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const date = prompt('Enter schedule date (YYYY-MM-DD HH:MM):',
                                      new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16).replace('T', ' '));
                                    if (date) {
                                      handleSchedule(notification.id, new Date(date).toISOString());
                                    }
                                  }}
                                  title="Schedule"
                                >
                                  <Calendar className="w-4 h-4 text-info" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedNotification(notification);
                                    setShowCreateModal(true);
                                  }}
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDelete(notification.id);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <DataTablePagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={filteredNotifications.length}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onPageSizeChange={(value) => { setPageSize(value); setPage(1); }}
                itemLabel="notifications"
                pageSizeTestId="notifications-page-size"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateNotificationModal
          token={token}
          notification={selectedNotification}
          canManagePlan={canManagePlan}
          notificationCapacity={notificationCapacity}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedNotification(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedNotification(null);
            refreshNotificationData();
          }}
        />
      )}

      <NotificationDetailsSheet
        notification={detailNotification}
        open={detailOpen}
        loading={detailLoading}
        error={detailError}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) {
            setDetailNotification(null);
            setDetailError(null);
          }
        }}
        getTypeIcon={getTypeIcon}
        getStatusBadge={getStatusBadge}
        getTargetLabel={getTargetLabel}
      />
    </PageShell>
  );
}

function CreateNotificationModal({ token, notification, canManagePlan, notificationCapacity, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: notification?.title || '',
    message: notification?.message || '',
    type: notification?.type || 'info',
    target_type: notification?.target_type || 'all',
    // Groups and users both live in target_ids, as GROUP/USER IDS — that is the only
    // field the API accepts. Group selections used to be kept in a separate
    // `target_groups` array holding group *names*, which the backend does not declare
    // and Pydantic silently discarded, so every group-targeted send stored
    // target_ids=NULL and reached nobody (EIP-304).
    target_ids: notification?.target_ids || [],
    channel: notification?.channel || 'in_app',
    action_url: notification?.action_url || '',
    action_label: notification?.action_label || '',
  });
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  
  // Fetch groups and users for selection
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch groups
        const groupsRes = await fetch(`${API_URL}/api/groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (groupsRes.ok) {
          const data = await groupsRes.json();
          setGroups(data);
        }
        
        // Fetch users
        const usersRes = await fetch(`${API_URL}/api/employees?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (usersRes.ok) {
          const data = await usersRes.json();
          setUsers(data.employees || []);
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, [token]);
  
  const filteredUsers = users.filter(u => 
    !userSearch || 
    (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSave = async (andSend = false, andSchedule = false) => {
    if (andSend && isCapacityAtLimit(notificationCapacity)) {
      showNotificationLimitToast(buildNotificationCapacityMessage(notificationCapacity), canManagePlan);
      return;
    }

    const actionUrl = normalizeActionUrl(formData.action_url);
    if (actionUrl === null) {
      toast.error('Link URL must be a valid link, e.g. https://example.com/page');
      return;
    }
    if (actionUrl !== formData.action_url) {
      setFormData((prev) => ({ ...prev, action_url: actionUrl }));
    }

    if (andSend) setSendLoading(true);
    else setLoading(true);

    try {
      // target_ids carries the audience for both 'groups' and 'users'; only the
      // tenant-wide 'all' has no ids. This line previously blanked target_ids for
      // every target_type except 'users', which is what silently broke group sends.
      const payload = {
        ...formData,
        action_url: actionUrl,
        target_ids: formData.target_type === 'all' ? [] : formData.target_ids,
      };

      const url = notification
        ? `${API_URL}/api/notifications/${notification.id}`
        : `${API_URL}/api/notifications`;
      
      const response = await fetch(url, {
        method: notification ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const savedNotification = await response.json();
        const notifId = savedNotification.id || notification?.id;
        
        if (andSend && notifId) {
          // Send immediately
          const sendRes = await fetch(`${API_URL}/api/notifications/${notifId}/send`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (sendRes.ok) {
            reportSendResult(await sendRes.json());
          } else {
            const error = await readErrorPayload(sendRes, 'Failed to send notification');
            if (isNotificationLimitError(error)) {
              showNotificationLimitToast(error, canManagePlan);
            } else {
              toast.error(parseErrorDetail(error).message || 'Failed to send notification');
            }
            return;
          }
        } else if (andSchedule && notifId && scheduledAt) {
          // Schedule
          const schedRes = await fetch(`${API_URL}/api/notifications/${notifId}/schedule?scheduled_at=${encodeURIComponent(new Date(scheduledAt).toISOString())}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (schedRes.ok) {
            toast.success('Notification scheduled!');
          } else {
            toast.error('Failed to schedule notification');
            return;
          }
        } else {
          toast.success(notification ? 'Notification updated!' : 'Draft saved');
        }
        onSuccess();
      } else {
        const error = await readErrorPayload(response, 'Failed to save notification');
        if (isNotificationLimitError(error)) {
          showNotificationLimitToast(error, canManagePlan);
        } else {
          toast.error(parseErrorDetail(error).message || 'Failed to save notification');
        }
      }
    } catch (error) {
      console.error('Failed to save notification:', error);
      toast.error('Failed to save notification');
    } finally {
      setLoading(false);
      setSendLoading(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/50 p-4"
      data-testid="notification-modal-overlay"
    >
      <div
        className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
        data-testid="notification-modal"
      >
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {notification ? 'Edit Notification' : 'Create Notification'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Open Enrollment Reminder"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Write your notification message..."
                rows={3}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                required
              />
            </div>

            {/* Type and Target in 2 columns */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {[
                    { value: 'info', label: 'Information' },
                    { value: 'reminder', label: 'Reminder' },
                    { value: 'urgent', label: 'Urgent' },
                    { value: 'success', label: 'Success' },
                  ].map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target Audience
                </label>
                <select
                  value={formData.target_type}
                  onChange={(e) => setFormData({ ...formData, target_type: e.target.value, target_ids: [] })}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">All Users</option>
                  <option value="groups">Specific Groups</option>
                  <option value="users">Specific Users</option>
                </select>
              </div>
            </div>
            
            {/* Group Selection */}
            {formData.target_type === 'groups' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Groups
                </label>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-32 overflow-y-auto">
                  {groups.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">No groups available</p>
                  ) : (
                    groups.map((group) => (
                      <label 
                        key={group.id || group.name} 
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.target_ids.includes(group.id)}
                          onChange={(e) => {
                            // Must be group.id: the backend resolves membership with
                            // `WHERE ug.group_id = ?`. This used to send group.name.
                            if (e.target.checked) {
                              setFormData({ ...formData, target_ids: [...formData.target_ids, group.id] });
                            } else {
                              setFormData({ ...formData, target_ids: formData.target_ids.filter(g => g !== group.id) });
                            }
                          }}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{group.name || group.displayName}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* User Selection */}
            {formData.target_type === 'users' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Users
                </label>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full mb-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-32 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">No users found</p>
                  ) : (
                    filteredUsers.slice(0, 50).map((user) => (
                      <label 
                        key={user.id || user.email} 
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.target_ids.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, target_ids: [...formData.target_ids, user.id] });
                            } else {
                              setFormData({ ...formData, target_ids: formData.target_ids.filter(id => id !== user.id) });
                            }
                          }}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{user.name || user.email}</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.target_ids.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">{formData.target_ids.length} user(s) selected</p>
                )}
              </div>
            )}

            {/* Delivery Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Delivery Channel
              </label>
              <div className="flex gap-4">
                {[
                  { value: 'in_app', label: 'Eva People' },
                  { value: 'email', label: 'Email' },
                  { value: 'both', label: 'Both' },
                ].map((channel) => (
                  <label key={channel.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="channel"
                      value={channel.value}
                      checked={formData.channel === channel.value}
                      onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                      className="text-purple-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{channel.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Call to Action (optional) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Link URL <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="url"
                  value={formData.action_url}
                  onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
                  placeholder="https://example.com/page"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Text <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.action_label}
                  onChange={(e) => setFormData({ ...formData, action_label: e.target.value })}
                  placeholder="e.g., Learn More"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Schedule <span className="text-gray-400 font-normal">(optional - leave empty to send now or save as draft)</span>
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSave(false, false)}
              disabled={loading || sendLoading || !formData.title || !formData.message}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Draft'}
            </button>
            {scheduledAt ? (
              <button
                type="button"
                onClick={() => handleSave(false, true)}
                disabled={loading || sendLoading || !formData.title || !formData.message}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                <Calendar className="w-4 h-4" />
                Schedule
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleSave(true, false)}
                disabled={loading || sendLoading || !formData.title || !formData.message}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {sendLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
